import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { Input } from "components/form_components/Inputs";
import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import { sendGa4Event } from "../../lib/ga4Tracking";
import { useCreateAccount } from "../../lib/hooks/api/onboarding/useCreateAccount";
import { ACCOUNT_SETUP_FORM, fieldInteractionEvent } from "./tracking";

import css from "./CreateAccountForm.module.scss";

export type FormData = {
  jobTitle: string;
  firstName: string;
  lastName: string;
  companyName: string;

  // Note that these won't be used in account creation,
  // but it's useful to have it for displaying it to user
  email: string;
  partnerProgrammeName: string | null;
};

type Props = {
  email: string;
  readonlyCompany: boolean;
  initialValues: FormData;
};

export function CreateAccountForm(props: Props) {
  const { initialValues, readonlyCompany } = props;

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting },
    watch,
  } = useForm<FormData>({
    defaultValues: initialValues,
    mode: "onBlur",
  });

  const previousFormValue = useRef<Partial<FormData>>();

  const { mutate, isError, isLoading } = useCreateAccount({
    onSuccess: () => {
      window.location.reload();
    },
  });

  const onSubmit = handleSubmit((data) => {
    sendGa4Event({
      event: "continue_click",
      form: ACCOUNT_SETUP_FORM,
    });
    const dataToSubmit = { ...data };
    // if readonlyCompany is true, add the companyName from initialValues as it gets removed as the input is disabled
    if (
      readonlyCompany &&
      dataToSubmit.companyName === undefined &&
      initialValues.companyName !== undefined
    ) {
      dataToSubmit.companyName = initialValues.companyName;
    }
    // if partnerProgrammeName is not provided, use the initial value
    if (
      initialValues.partnerProgrammeName &&
      (!dataToSubmit.partnerProgrammeName || dataToSubmit.partnerProgrammeName === "")
    ) {
      dataToSubmit.partnerProgrammeName = initialValues.partnerProgrammeName;
    }

    const storedUtmParams = localStorage.getItem("utm_params");
    const storedSeoParams = localStorage.getItem("seo_params");
    const parsedUtmParams = storedUtmParams ? JSON.parse(storedUtmParams) : null;
    const parsedSeoParams = storedSeoParams ? JSON.parse(storedSeoParams) : null;

    // Check if we have valid UTM parameters
    const hasValidUtmParams =
      parsedUtmParams &&
      typeof parsedUtmParams === "object" &&
      Object.keys(parsedUtmParams).length > 0 &&
      Object.values(parsedUtmParams).some((value) => value !== null);

    // Process UTM parameters if valid
    const utmParams = hasValidUtmParams
      ? (Object.fromEntries(
          Object.entries(parsedUtmParams).map(([key, value]) => [
            key,
            typeof value === "string" ? decodeURIComponent(value) : value,
          ]),
        ) as Record<string, string | null> | null)
      : null;

    mutate({
      formState: dataToSubmit,
      utmParams: utmParams,
      seoParams: parsedSeoParams,
    });
  });

  useEffect(() => {
    // they have started interacting with the form
    if (isDirty) {
      sendGa4Event({
        event: "account_setup_form_start",
        form: ACCOUNT_SETUP_FORM,
      });
    }
  }, [isDirty]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        // if the field was empty and now it's not
        if (
          (!previousFormValue.current || !previousFormValue.current[name]) &&
          value[name] !== undefined
        ) {
          fieldInteractionEvent(value[name]?.toString() || "", name);
        }
      }
      previousFormValue.current = value;
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <form onSubmit={onSubmit} className={css.onboardingForm} id="create-account">
      {isError && (
        <Alert type="error">
          An error occurred creating your account - please try again or reach out to{" "}
          <a href="mailto:team@stotles.com">team@stotles.com</a> if this persists.
        </Alert>
      )}
      <Input<FormData>
        name="firstName"
        placeholder="Ari"
        label="First name"
        control={control}
        size="large"
        rules={{ required: { value: true, message: "Please fill out your first name" } }}
      />
      <Input<FormData>
        name="lastName"
        placeholder="Stotle"
        label="Last name"
        control={control}
        size="large"
        rules={{ required: { value: true, message: "Please fill out your last name" } }}
      />
      <Input<FormData>
        name="companyName"
        placeholder="Phylosofi"
        label="Company name"
        disabled={readonlyCompany}
        readOnly={readonlyCompany}
        control={control}
        size="large"
        rules={{ required: { value: true, message: "Please fill out your company name" } }}
      />

      <Input<FormData>
        name="jobTitle"
        placeholder="Account Manager"
        label="Job title"
        control={control}
        size="large"
        rules={{ required: { value: true, message: "Please fill out your job title" } }}
      />
      {initialValues.partnerProgrammeName && (
        <Input<FormData>
          name="partnerProgrammeName"
          placeholder="Partner programme name"
          label="Partner programme name"
          disabled
          readOnly
          control={control}
          size="large"
          required
        />
      )}
      <Button
        type="submit"
        loading={isSubmitting || isLoading}
        disabled={!isDirty || !isValid}
        className={css.submitButton}
      >
        Continue
      </Button>
    </form>
  );
}
